import { useState } from "react";
import { NavLink, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  LayoutDashboard,
  Store,
  UtensilsCrossed,
  BarChart3,
  Settings,
  LogOut,
  Globe,
  Check,
  Sparkles,
  ExternalLink,
  Heart,
  ChefHat,
  Bike,
  ShoppingBag,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/logo";
import { useI18n } from "@/i18n";
import type { Lang } from "@/i18n";
import { getAiCredits, getVenue } from "@/api/endpoints";
import { BuyCreditsDialog } from "@/components/buy-credits-dialog";

const LANGUAGES: { code: Lang; label: string; flag: string }[] = [
  { code: "tr", label: "Türkçe", flag: "🇹🇷" },
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "de", label: "Deutsch", flag: "🇩🇪" },
  { code: "fr", label: "Français", flag: "🇫🇷" },
  { code: "es", label: "Español", flag: "🇪🇸" },
  { code: "ar", label: "العربية", flag: "🇸🇦" },
  { code: "ru", label: "Русский", flag: "🇷🇺" },
];
import type { TranslationKey } from "@/i18n";

const mainLinks: { to: string; icon: typeof LayoutDashboard; labelKey: TranslationKey }[] = [
  { to: "/", icon: LayoutDashboard, labelKey: "dashboard" },
  { to: "/venues", icon: Store, labelKey: "venues" },
];

// 13 ayrı link → 5 ana sekme. Her sekme bir "section" sayfası; içerideki
// tab navigation alt sayfaları yönetiyor (bkz. components/section-shell).
//
// `requiresOrders: true` olan grup `venue.orderEnabled !== false` ise
// gösteriliyor — QR-only restoranlar (sadece menü, sipariş yok) Sipariş
// grubunu sidebar'da hiç görmüyor.
type VenueLink = {
  to: string;
  icon: typeof LayoutDashboard;
  labelKey: TranslationKey;
  requiresOrders?: boolean;
  /** `venue.yemeksepetiEnabled` true ise göster. Admin-only beta gating. */
  requiresYemeksepeti?: boolean;
  /** `venue.trendyolgoEnabled` true ise göster. Admin-only beta gating. */
  requiresTrendyolgo?: boolean;
};
const venueLinks: VenueLink[] = [
  { to: "menu", icon: UtensilsCrossed, labelKey: "section_menu" },
  { to: "orders", icon: ChefHat, labelKey: "section_orders", requiresOrders: true },
  { to: "customers", icon: Heart, labelKey: "section_customers" },
  { to: "reports", icon: BarChart3, labelKey: "section_reports" },
  { to: "yemeksepeti", icon: Bike, labelKey: "section_yemeksepeti", requiresYemeksepeti: true },
  { to: "trendyolgo", icon: ShoppingBag, labelKey: "section_trendyolgo", requiresTrendyolgo: true },
  { to: "settings", icon: Settings, labelKey: "venue_settings" },
];

export function Sidebar({
  onLogout,
  onNavigate,
  planType,
  venueLogo,
  venueName,
  staffRole,
}: {
  onLogout: () => void;
  onNavigate?: () => void;
  planType?: string;
  venueLogo?: string;
  venueName?: string;
  staffRole?: string | null;
}) {
  const { venueId } = useParams();
  const { lang, setLang, t } = useI18n();
  const [langOpen, setLangOpen] = useState(false);
  const [buyCreditsOpen, setBuyCreditsOpen] = useState(false);

  const { data: creditData } = useQuery({
    queryKey: ["ai-credits", venueId],
    queryFn: () => getAiCredits(venueId!),
    enabled: !!venueId && planType !== "FREE",
    refetchInterval: 60000,
  });

  const { data: venue } = useQuery({
    queryKey: ["venue", venueId],
    queryFn: () => getVenue(venueId!),
    enabled: !!venueId,
  });

  const menuUrl = venue
    ? venue.customDomain && (venue as unknown as Record<string, unknown>).customDomainStatus === "active"
      ? `https://${venue.customDomain}`
      : `https://menu.heremenu.app/${venue.slug}`
    : null;

  const handleClick = () => onNavigate?.();

  const isStaff = !!staffRole;
  // Yeni 5-grup yapısında staff izinleri:
  //  - waiter → sadece Sipariş (masalar)
  //  - cashier → Sipariş + Raporlar (satışlar görür)
  // İşletme sahibi ise hepsini görür ama orderEnabled false ise Sipariş gizli.
  const orderEnabled =
    (venue as unknown as Record<string, unknown>)?.orderEnabled !== false;
  const yemeksepetiEnabled =
    !!(venue as unknown as Record<string, unknown>)?.yemeksepetiEnabled;
  const trendyolgoEnabled =
    !!(venue as unknown as Record<string, unknown>)?.trendyolgoEnabled;
  const staffAllowed = staffRole === "cashier" ? ["orders", "reports"] : ["orders"];
  const filteredVenueLinks = isStaff
    ? venueLinks.filter((l) => staffAllowed.includes(l.to))
    : venueLinks.filter((l) => {
        if (l.requiresOrders && !orderEnabled) return false;
        if (l.requiresYemeksepeti && !yemeksepetiEnabled) return false;
        if (l.requiresTrendyolgo && !trendyolgoEnabled) return false;
        return true;
      });

  return (
    <aside className="flex h-screen w-64 flex-col border-r bg-card">
      <div className="flex h-16 items-center gap-3 border-b px-6">
        {venueLogo ? (
          <img src={venueLogo} alt="" className="size-9 rounded-xl object-cover" />
        ) : venueName ? (
          // Restoran context'inde venue logo yoksa, venue isminin ilk
          // harfi şeklen "venue placeholder" olarak çalışır — burada
          // marka logosu göstermek venue kimliğini bastırırdı.
          <div className="flex size-9 items-center justify-center rounded-xl bg-primary">
            <span className="text-lg font-bold text-primary-foreground">{venueName[0]}</span>
          </div>
        ) : (
          // Hiç venue context'i yok (genel dashboard) — marka sembolünü göster.
          <Logo size="md" variant="mark" />
        )}
        <span className="text-lg font-bold truncate">{venueName || "HereMenu"}</span>
        {planType && !isStaff && (
          <span className={`ml-auto shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase ${
            planType === "PRO" ? "bg-primary/10 text-primary" :
            "bg-muted text-muted-foreground"
          }`}>{planType}</span>
        )}
        {isStaff && (
          <span className="ml-auto shrink-0 rounded-md bg-primary/10 px-1.5 py-0.5 text-[10px] font-bold text-primary uppercase">
            {t(staffRole === "waiter" ? "staff_role_waiter" : "staff_role_cashier")}
          </span>
        )}
      </div>

      <nav className="flex-1 space-y-1 overflow-y-auto p-3">
        {!isStaff && mainLinks.map(({ to, icon: Icon, labelKey }) => (
          <NavLink
            key={to}
            to={to}
            end={to === "/"}
            onClick={handleClick}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                isActive
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
              }`
            }
          >
            <Icon className="size-5" />
            {t(labelKey)}
          </NavLink>
        ))}

        {venueId && (
          <>
            {!isStaff && <div className="my-3 border-t" />}
            <p className="px-3 py-1 text-xs font-semibold uppercase text-muted-foreground truncate">
              {venueName || t("venues")}
            </p>
            {filteredVenueLinks.map(({ to, icon: Icon, labelKey }) => (
              <NavLink
                key={to}
                to={`/venues/${venueId}/${to}`}
                onClick={handleClick}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-primary/10 text-primary"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  }`
                }
              >
                <Icon className="size-5" />
                {t(labelKey)}
              </NavLink>
            ))}

            {/* View Menu */}
            {!isStaff && menuUrl && (
              <>
                <div className="my-3 border-t" />
                <a
                  href={menuUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleClick}
                  className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-primary hover:bg-primary/10 transition-colors"
                >
                  <ExternalLink className="size-5" />
                  {t("view_menu")}
                </a>
              </>
            )}

            {/* AI Credits */}
            {!isStaff && planType !== "FREE" && creditData !== undefined && (
              <>
                <div className="my-3 border-t" />
                <button
                  onClick={() => setBuyCreditsOpen(true)}
                  className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors hover:bg-muted ${
                    (creditData?.credits ?? 0) === 0
                      ? "text-destructive"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Sparkles className="size-5" />
                  <span className="flex-1 text-left">{t("ai_credits")}</span>
                  <span className={`rounded-md px-1.5 py-0.5 text-[11px] font-bold ${
                    (creditData?.credits ?? 0) === 0
                      ? "bg-destructive/10 text-destructive"
                      : "bg-primary/10 text-primary"
                  }`}>
                    {creditData?.credits ?? 0}
                  </span>
                </button>
              </>
            )}
          </>
        )}
      </nav>

      <div className="border-t p-3">
        {/* Language selector */}
        <div className="relative mb-2">
          <button
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            onClick={() => setLangOpen(!langOpen)}
          >
            <Globe className="size-5" />
            {LANGUAGES.find((l) => l.code === lang)?.flag} {LANGUAGES.find((l) => l.code === lang)?.label}
          </button>
          {langOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setLangOpen(false)} />
              <div className="absolute bottom-full left-0 right-0 z-50 mb-1 rounded-lg border bg-card shadow-xl overflow-hidden">
                <div className="px-3 py-2 text-xs font-semibold text-muted-foreground border-b">
                  {t("select_language")}
                </div>
                {LANGUAGES.map((l) => (
                  <button
                    key={l.code}
                    className="flex w-full items-center gap-3 px-3 py-2.5 text-sm hover:bg-muted transition-colors"
                    onClick={() => { setLang(l.code); setLangOpen(false); }}
                  >
                    <span>{l.flag}</span>
                    <span className="flex-1 text-left">{l.label}</span>
                    {lang === l.code && <Check className="size-4 text-primary" />}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {!isStaff && (
          <NavLink
            to="/settings"
            onClick={handleClick}
            className={({ isActive }) =>
              `mb-1 flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                isActive
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
              }`
            }
          >
            <Settings className="size-5" />
            {t("membership_settings")}
          </NavLink>
        )}
        <Button
          variant="ghost"
          className="w-full justify-start gap-3 text-muted-foreground"
          onClick={() => {
            onLogout();
            handleClick();
          }}
        >
          <LogOut className="size-5" />
          {t("logout")}
        </Button>
      </div>

      <div className="px-6 py-2 text-center">
        <a href="https://heremenu.app" target="_blank" rel="noopener noreferrer" className="text-[10px] text-muted-foreground/50 hover:text-muted-foreground transition-colors">
          Powered by HereMenu
        </a>
      </div>

      <BuyCreditsDialog open={buyCreditsOpen} onClose={() => setBuyCreditsOpen(false)} />
    </aside>
  );
}
